import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl, errMsg, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, Notice } from "@/components/RequireAuth";
import StatusBadge from "@/components/StatusBadge";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Scale, SearchX } from "lucide-react";

const Evidence = ({ urls }) =>
  urls?.length ? (
    <div className="mt-4 grid grid-cols-3 gap-2">
      {urls.map((u) => <a key={u} href={imgUrl(u)} target="_blank" rel="noreferrer"><img src={imgUrl(u)} alt="evidence" className="aspect-square w-full rounded-xl border object-cover" /></a>)}
    </div>
  ) : <p className="mt-3 text-xs text-stone-400">No photos attached</p>;

function Statement({ who, name, date, text, urls, tone, testid }) {
  return (
    <div className={`rounded-3xl border p-6 ${tone}`} data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{who}</p>
      <p className="mt-1 font-semibold">{name} <span className="font-normal text-stone-500">· {fmtDate(date)}</span></p>
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-stone-800">{text}</p>
      <Evidence urls={urls} />
    </div>
  );
}

function HostReply({ c, onDone }) {
  const [text, setText] = useState("");
  const [evidence, setEvidence] = useState([]);
  const send = async () => {
    try {
      await api.post(`/complaints/${c.complaint_id}/respond`, { text, evidence });
      toast.success("Reply sent to the FairStay team");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4 rounded-3xl border bg-white p-6" data-testid="host-reply-form">
      <p className="font-semibold">Your side of the story</p>
      <Textarea data-testid="host-reply-input" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Explain what happened. Check-in photos, messages and receipts help." />
      <PhotoUploader value={evidence} onChange={setEvidence} kind="evidence" max={10} testid="host-reply-evidence" />
      <Button data-testid="host-reply-submit" disabled={text.trim().length < 5} onClick={send} className="rounded-full">Send reply</Button>
    </div>
  );
}

function AdminDecision({ c, onDone }) {
  const remaining = c.booking.total - (c.booking.refunded_amount || 0);
  const [decision, setDecision] = useState("dismissed");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/complaints/${c.complaint_id}/decide`, { decision, refund_amount: amount ? Number(amount) : null, note });
      toast.success("Decision saved");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4 rounded-3xl border-2 border-stone-900 bg-white p-6" data-testid="admin-decision-panel">
      <p className="font-semibold">Admin decision</p>
      <p className="text-sm text-stone-600">Paid {money(c.booking.total)} · refundable {money(remaining)}. Default to the host unless the guest's evidence clearly supports the complaint.</p>
      <RadioGroup value={decision} onValueChange={setDecision} className="gap-3">
        {[["dismissed", "Dismiss complaint (back the host)"], ["partial_refund", "Partial refund"], ["full_refund", `Full refund (${money(remaining)})`]].map(([v, l]) => (
          <label key={v} className="flex items-center gap-3 text-sm"><RadioGroupItem value={v} data-testid={`admin-decision-${v}`} />{l}</label>
        ))}
      </RadioGroup>
      {decision === "partial_refund" && <div className="space-y-2"><Label>Refund amount (USD)</Label><Input data-testid="admin-refund-amount" type="number" min="1" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>}
      <Textarea data-testid="admin-decision-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reasoning shared with both sides" />
      <Button data-testid="admin-complaint-decide-button" disabled={busy || (decision === "partial_refund" && !amount)} onClick={decide} className="rounded-full">{busy ? "Saving…" : "Confirm decision"}</Button>
    </div>
  );
}

export default function ComplaintDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: c, isLoading, error } = useQuery({ queryKey: ["complaint", id], queryFn: () => api.get(`/complaints/${id}`).then((r) => r.data) });
  const reload = () => qc.invalidateQueries({ queryKey: ["complaint", id] });
  if (isLoading) return <Spinner />;
  if (error) return <Notice icon={SearchX} title="Complaint not found" testid="complaint-not-found" />;
  const isHost = user.user_id === c.host_id;
  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-10 sm:px-6 lg:px-8" data-testid="complaint-detail">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={c.status} testid="complaint-status" />
        {c.decision && <StatusBadge status={c.decision} testid="complaint-decision" />}
      </div>
      <h1 className="text-3xl font-bold tracking-tight">{c.category}</h1>
      <p className="text-sm text-stone-600">
        <Link to={`/listings/${c.listing_id}`} className="font-medium text-primary hover:underline">{c.listing?.title}</Link> · {fmtDate(c.booking.check_in)} → {fmtDate(c.booking.check_out)} · paid {money(c.booking.total)}
      </p>
      <div className="flex gap-3 rounded-2xl bg-stone-900 p-5 text-sm text-stone-100" data-testid="complaint-policy-banner">
        <Scale className="h-5 w-5 shrink-0 text-emerald-300" />
        <p>
          {isHost || user.is_admin
            ? "Host protection: we back the host unless the guest's evidence clearly supports the complaint. Both sides can submit statements and photos, and a person on our team decides."
            : "Both sides can submit statements and photos. A person on our team reviews everything and decides based on the evidence."}
        </p>
      </div>
      <Statement who="Guest's complaint" name={c.guest?.name} date={c.created_at} text={c.description} urls={c.evidence} tone="bg-white" testid="complaint-guest-statement" />
      {c.host_response ? (
        <Statement who="Host's reply" name={c.host?.name} date={c.host_response.at} text={c.host_response.text} urls={c.host_response.evidence} tone="bg-teal-50/60 border-teal-100" testid="complaint-host-statement" />
      ) : isHost && c.status !== "resolved" ? (
        <HostReply c={c} onDone={reload} />
      ) : (
        <p className="rounded-2xl border border-dashed p-5 text-sm text-stone-500" data-testid="complaint-awaiting-host">Waiting for the host's reply.</p>
      )}
      {c.status === "resolved" && (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6" data-testid="complaint-resolution">
          <p className="font-semibold">Decision: {c.decision === "dismissed" ? "Complaint dismissed" : `${c.decision === "full_refund" ? "Full" : "Partial"} refund of ${money(c.refund_amount)} to the guest's card`}</p>
          {c.admin_note && <p className="mt-2 text-sm text-stone-700">{c.admin_note}</p>}
        </div>
      )}
      {user.is_admin && c.status !== "resolved" && <AdminDecision c={c} onDone={reload} />}
    </div>
  );
}
